import { useState } from "react";
import { Mail, Copy, Check, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";

const EMAIL = "bleureefobermayr@gmail.com";

export default function SendYourStory() {
  const { lang } = useLang();
  const [copied, setCopied] = useState(false);

  const T = lang === "fr" ? FR : EN;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success(T.copied);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const subject = encodeURIComponent(lang === "fr"
    ? "Ma proposition d'histoire pour WorldMysteriesMap"
    : "My story submission for WorldMysteriesMap");
  const body = encodeURIComponent(T.emailBodyTemplate);
  const mailto = `mailto:${EMAIL}?subject=${subject}&body=${body}`;

  return (
    <section className="relative border-t border-zinc-200 bg-gradient-to-b from-white to-stone-50 px-6 md:px-12 py-16" data-testid="send-story-section">
      <div className="max-w-4xl mx-auto">
        <div className="mb-4 flex items-center gap-3 text-[#8b6f3d]">
          <div className="h-px w-12 bg-[#8b6f3d]/40" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">
            {lang === "fr" ? "Mise à jour régulière" : "Regular updates"}
          </span>
        </div>

        <h2 className="font-serif-mystery text-3xl md:text-4xl text-zinc-900 mb-5 leading-tight">
          {T.updatesTitle}
        </h2>
        <p className="text-zinc-600 leading-relaxed max-w-2xl mb-10">
          {T.updatesBody}
        </p>

        <div className="border border-[#8b6f3d]/30 bg-amber-50/40 rounded-2xl p-6 md:p-8">
          <h3 className="font-serif-mystery text-2xl text-[#8b6f3d] mb-3">{T.ctaTitle}</h3>
          <p className="text-zinc-700 mb-6 leading-relaxed">{T.ctaBody}</p>

          <div className="flex flex-wrap items-center gap-3 mb-5">
            <a href={mailto} data-testid="send-story-mail-btn">
              <Button size="lg" className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-12 px-6">
                <Send size={16} className="mr-2" />
                {T.sendBtn}
              </Button>
            </a>
            <Button
              variant="outline"
              size="lg"
              onClick={copyEmail}
              data-testid="send-story-copy-btn"
              className="rounded-full h-12 px-5 border-zinc-300 hover:bg-white text-zinc-800"
            >
              {copied ? <Check size={16} className="mr-2 text-emerald-600" /> : <Copy size={16} className="mr-2" />}
              {copied ? T.copied : T.copyEmail}
            </Button>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 text-sm text-zinc-600 hover:text-[#8b6f3d] font-mono-data">
              <Mail size={14} /> {EMAIL}
            </a>
          </div>

          <div className="pt-4 border-t border-[#8b6f3d]/20">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              {T.whatToSend}
            </div>
            <ul className="grid sm:grid-cols-2 gap-1.5 text-sm text-zinc-700">
              {T.items.map((it, i) => (<li key={i} className="flex items-start gap-2"><span className="text-[#8b6f3d]">•</span>{it}</li>))}
            </ul>
            <p className="text-xs text-zinc-500 italic mt-4">{T.disclaimer}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const FR = {
  updatesTitle: "De nouveaux mystères ajoutés régulièrement",
  updatesBody: "De nouveaux mystères, lieux historiques et histoires inexpliquées sont ajoutés régulièrement sur WorldMysteriesMap.com. Revenez souvent pour découvrir les derniers ajouts.",
  ctaTitle: "Envoyez votre histoire",
  ctaBody: "Vous connaissez une histoire mystérieuse, un lieu hanté, une légende locale ou un événement inexpliqué ? Envoyez-nous votre histoire. Après vérification, elle pourrait être ajoutée sur la carte WorldMysteriesMap.com.",
  sendBtn: "Envoyer votre histoire",
  copyEmail: "Copier l'adresse",
  copied: "Adresse copiée",
  whatToSend: "Vous pouvez envoyer",
  items: ["Le nom du lieu", "La localisation", "L'histoire complète", "Des photos ou documents disponibles", "Des sources ou références", "Toute information supplémentaire utile"],
  disclaimer: "Les histoires envoyées par les utilisateurs sont vérifiées avant publication afin de maintenir la qualité et la crédibilité du site.",
  emailBodyTemplate: `Bonjour,

Je souhaite proposer une histoire pour WorldMysteriesMap.com.

• Nom du lieu :
• Localisation (ville, pays, coordonnées si possible) :
• Type de mystère :
• Histoire complète :

• Sources / références (livres, articles, archives) :
• Photos ou documents joints : (oui / non)
• Informations supplémentaires :

Merci !
`,
};

const EN = {
  updatesTitle: "New mysteries added regularly",
  updatesBody: "New mysteries, historical places and unexplained stories are added regularly on WorldMysteriesMap.com. Come back often to discover the latest additions.",
  ctaTitle: "Send your story",
  ctaBody: "Do you know a mysterious story, a haunted place, a local legend or an unexplained event? Send us your story. After verification, it could be added to the WorldMysteriesMap.com map.",
  sendBtn: "Send your story",
  copyEmail: "Copy email",
  copied: "Email copied",
  whatToSend: "You can send",
  items: ["Name of the place", "Location", "The full story", "Photos or documents available", "Sources or references", "Any useful additional information"],
  disclaimer: "Stories submitted by users are verified before publication to maintain the quality and credibility of the site.",
  emailBodyTemplate: `Hello,

I would like to submit a story for WorldMysteriesMap.com.

• Name of the place:
• Location (city, country, coordinates if possible):
• Type of mystery:
• Full story:

• Sources / references (books, articles, archives):
• Photos or documents attached: (yes / no)
• Additional information:

Thank you!
`,
};
